"use client";
import { Menu, X, Github, ArrowUpRight } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

import { AuthLink } from "@/components/shared/auth-link";
import { useAppConfig } from "@/lib/config";
import Logo from "@/lib/icons/Logo";

import {
	Button,
	cn,
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@meetploy/storybook";

import { ThemeToggle } from "./theme-toggle";

import type { ReactNode } from "react";

const itemClassName =
	"group flex h-full select-none items-baseline gap-3 rounded-none px-5 py-4 no-underline outline-none transition-colors hover:bg-muted/60 focus-visible:bg-muted";

function ListItem({
	title,
	href,
	children,
	external,
	index,
}: {
	title: string;
	href: string;
	children: ReactNode;
	external?: boolean;
	index: number;
}) {
	return (
		<li className="border-b border-r border-border [&:nth-child(2n)]:border-r-0 [&:nth-last-child(-n+2)]:border-b-0">
			<NavigationMenuLink
				render={
					external ? (
						<a
							href={href}
							target="_blank"
							rel="noopener noreferrer"
							className={itemClassName}
						/>
					) : (
						<Link href={href} prefetch={true} className={itemClassName} />
					)
				}
			>
				<span className="font-mono text-[0.625rem] tabular-nums text-muted-foreground/60">
					{String(index).padStart(2, "0")}
				</span>
				<span className="flex-1">
					<span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest">
						{title}
						<ArrowUpRight className="size-3 -translate-x-0.5 opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:opacity-100" />
					</span>
					<span className="mt-1.5 block text-sm leading-snug text-muted-foreground">
						{children}
					</span>
				</span>
			</NavigationMenuLink>
		</li>
	);
}

export const Navbar = ({ children }: { children?: React.ReactNode }) => {
	const config = useAppConfig();

	const menuItems = [
		{ name: "Features", href: "/features", external: false },
		{ name: "Pricing", href: "/#pricing", external: false },
		{ name: "Docs", href: config.docsUrl ?? "", external: true },
		{ name: "Blog", href: "/blog", external: false },
	];

	const resourcesItems = [
		{ name: "Compare", href: "/compare" },
		{ name: "Changelog", href: "/changelog" },
		{ name: "Docs", href: config.docsUrl ?? "", external: true },
		{ name: "GitHub", href: config.githubUrl ?? "", external: true },
		{ name: "Contact Us", href: "mailto:contact@meetploy.com" },
	];

	const resourcesLinks: Array<{
		title: string;
		href: string;
		description: string;
		external?: boolean;
	}> = [
		{
			title: "Docs",
			href: config.docsUrl ?? "#",
			description:
				"Guides, API reference, and examples for workflows, sandboxes, and queues.",
			external: true,
		},
		{
			title: "Features",
			href: "/features",
			description:
				"Deploys, workers, databases, queues, workflows, cron, storage, sandboxes, AI, and auth.",
		},
		{
			title: "Compare",
			href: "/compare",
			description: "Ploy vs Vercel, Netlify, and Lovable, feature by feature.",
		},
		{
			title: "Changelog",
			href: "/changelog",
			description: "What's new in Ploy across releases.",
		},
		{
			title: "GitHub",
			href: config.githubUrl ?? "#",
			description: "Read the source, file an issue, or self-host it yourself.",
			external: true,
		},
		{
			title: "Contact sales",
			href: "mailto:contact@meetploy.com",
			description: "Dedicated infrastructure, security review, and SLAs.",
		},
	];

	const [menuState, setMenuState] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 50);
		};
		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	return (
		<header>
			<nav
				data-state={menuState && "active"}
				className="fixed z-20 w-full px-2 group"
			>
				<div
					className={cn(
						"mx-auto mt-2 max-w-6xl px-6 transition-all duration-300 lg:px-12",
						isScrolled &&
							"bg-background/80 max-w-6xl rounded-none border backdrop-blur-lg lg:px-5",
					)}
				>
					<div className="relative flex flex-wrap items-center justify-between gap-6 py-3 lg:gap-0 lg:py-4">
						{/* Logo and Title */}
						<div className="flex w-full justify-between lg:w-auto">
							<Link
								href="/"
								aria-label="home"
								className="flex items-center space-x-2"
								prefetch={true}
							>
								<Logo className="h-8 w-8 rounded-full text-foreground" />
								<span className="text-xl font-bold tracking-tight text-foreground">
									Ploy
								</span>
							</Link>

							<button
								onClick={() => {
									setMenuState(!menuState);
								}}
								aria-label={menuState ? "Close Menu" : "Open Menu"}
								className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden"
							>
								<Menu className="group-data-[state=active]:scale-0 group-data-[state=active]:opacity-0 size-6 duration-200" />
								<X className="absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 group-data-[state=active]:rotate-0 group-data-[state=active]:scale-100 group-data-[state=active]:opacity-100 duration-200" />
							</button>
						</div>

						<div className="m-auto hidden size-fit lg:block">
							<NavigationMenu>
								<NavigationMenuList className="flex gap-2 text-sm">
									{menuItems.map((item) => (
										<NavigationMenuItem key={item.name}>
											{item.external ? (
												<NavigationMenuLink
													render={
														<a
															href={item.href}
															target="_blank"
															rel="noopener noreferrer"
															className="text-muted-foreground hover:text-accent-foreground block duration-150 px-4 py-2"
														/>
													}
												>
													{item.name}
												</NavigationMenuLink>
											) : (
												<NavigationMenuLink
													render={
														<Link
															href={item.href}
															className="text-muted-foreground hover:text-accent-foreground block duration-150 px-4 py-2"
															prefetch={true}
														/>
													}
												>
													{item.name}
												</NavigationMenuLink>
											)}
										</NavigationMenuItem>
									))}

									<NavigationMenuItem>
										<NavigationMenuTrigger className="text-muted-foreground hover:text-accent-foreground px-4 py-2">
											Resources
										</NavigationMenuTrigger>
										<NavigationMenuContent className="p-0">
											<div className="w-[92vw] max-w-[40rem]">
												<NavigationMenuLink
													render={
														<a
															className="group flex select-none items-baseline gap-3 border-b border-border px-5 py-4 no-underline outline-none transition-colors hover:bg-muted/60 focus-visible:bg-muted"
															href={
																config.docsUrl
																	? `${config.docsUrl}/quick-start`
																	: "#"
															}
															target={config.docsUrl ? "_blank" : undefined}
															rel={
																config.docsUrl
																	? "noopener noreferrer"
																	: undefined
															}
														/>
													}
												>
													<span className="font-mono text-[0.625rem] tabular-nums text-muted-foreground/60">
														00
													</span>
													<span className="flex-1">
														<span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest">
															Quick Start
															<ArrowUpRight className="size-3 -translate-x-0.5 opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:opacity-100" />
														</span>
														<span className="mt-1.5 block text-sm leading-snug text-muted-foreground">
															Install the CLI, connect a repository, and push
															your first agent to production.
														</span>
													</span>
												</NavigationMenuLink>

												<ul className="grid grid-cols-1 sm:grid-cols-2">
													{resourcesLinks.map((link, i) => (
														<ListItem
															key={link.title}
															index={i + 1}
															title={link.title}
															href={link.href}
															external={link.external}
														>
															{link.description}
														</ListItem>
													))}
												</ul>
											</div>
										</NavigationMenuContent>
									</NavigationMenuItem>
								</NavigationMenuList>
							</NavigationMenu>
						</div>

						<div className="bg-background group-data-[state=active]:block lg:group-data-[state=active]:flex mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 rounded-none border p-6 shadow-lg md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent">
							<div className="lg:hidden">
								<ul className="space-y-6 text-base">
									{menuItems.map((item) => (
										<li key={item.name}>
											{item.external ? (
												<a
													href={item.href}
													target="_blank"
													rel="noopener noreferrer"
													className="text-muted-foreground hover:text-accent-foreground block duration-150"
												>
													{item.name}
												</a>
											) : (
												<Link
													href={item.href}
													className="text-muted-foreground hover:text-accent-foreground block duration-150"
													prefetch={true}
												>
													{item.name}
												</Link>
											)}
										</li>
									))}

									<li className="space-y-2">
										<div className="text-muted-foreground text-sm font-medium">
											Resources
										</div>
										<ul className="space-y-3 pl-4">
											{resourcesItems.map((item) => (
												<li key={item.name}>
													<Link
														href={item.href}
														className="text-muted-foreground hover:text-accent-foreground block duration-150"
														prefetch={true}
													>
														{item.name}
													</Link>
												</li>
											))}
										</ul>
									</li>

									{/* Mobile Social Icons */}
									<li className="flex items-center gap-4 pt-4 border-t border-border">
										<a
											href={config.githubUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="text-muted-foreground hover:text-accent-foreground p-2 rounded-md transition-colors"
											aria-label="GitHub"
										>
											<Github className="h-5 w-5" />
										</a>
										<a
											href={config.discordUrl}
											target="_blank"
											rel="noopener noreferrer"
											className="text-muted-foreground hover:text-accent-foreground p-2 rounded-md transition-colors"
											aria-label="Discord"
										>
											<svg
												className="h-5 w-5"
												viewBox="0 0 24 24"
												fill="currentColor"
											>
												<path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
											</svg>
										</a>
									</li>
								</ul>
							</div>

							<div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit items-center">
								{children}
								<div className="flex items-center gap-2 lg:order-first lg:mr-4">
									<a
										href={config.githubUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="text-muted-foreground hover:text-accent-foreground p-2 rounded-md transition-colors hidden lg:block"
										aria-label="GitHub"
									>
										<Github className="h-5 w-5" />
									</a>
									<a
										href={config.discordUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="text-muted-foreground hover:text-accent-foreground p-2 rounded-md transition-colors hidden lg:block"
										aria-label="Discord"
									>
										<svg
											className="h-5 w-5"
											viewBox="0 0 24 24"
											fill="currentColor"
										>
											<path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
										</svg>
									</a>
								</div>

								<Button
									className={cn("w-full md:w-fit")}
									render={<AuthLink href="/signup" />}
								>
									Get Started
								</Button>
								<ThemeToggle />
							</div>
						</div>
					</div>
				</div>
			</nav>
		</header>
	);
};
